import React from 'react'
import { ACTIVITY_STATEMENTS_ROUTE } from '@mobi/account/Areas/Statements/Utils/constants'
import { WITHDRAW_ROOT_ROUTE } from '@mobi/account/Areas/Withdraw/Utils'
import { routes as accountRoutes } from '@mobi/account/Utils'
import { spacing } from '@mobi/component-library/Foundations'
import { Icon } from '@mobi/component-library/Legacy/Icon'
import { SpinnerInlineStyled } from '@mobi/component-library/Legacy/Spinner/Spinner.styles'
import { PageHeader } from '@mobi/component-library/Navigation/PageHeader'
import { Constants, FeatureFlags } from '@mobi/settings'
import { isReactNativeApp } from '@mobi/web-native-comms/web'
import { close as modalClose } from '@classic/AppUtils/Framework/Intent/modal'
import { TAKE_A_BREAK_ROUTE } from '@core/Areas/Account/TakeABreak/constants'
import { openDeposit } from '@core/Areas/Deposit'
import { Button } from '@core/Components/Buttons/Button'
import { MainContainer } from '@core/Components/Containers/MainContainer'
import { Money } from '@core/Components/Text/Money'
import { queryKeys } from '@core/Data/ReactQuery/constants'
import { queryClient } from '@core/Data/ReactQuery/queryClient'
import { keys } from '@core/Services/Analytics/keys'
import { trackEvent } from '@core/Services/Analytics/track'
import { state$ as userAccountState$ } from '@core/State/UserAccount/userAccountDriver'
import { useFeature, useObservableImmutable } from '@core/Utils/hooks'
import {
  AccountBalanceStyled,
  AccountNumberBadgeStyled as Badge,
  BonusBalanceStyled,
  BonusBalanceTitleStyled,
  BonusBalanceValueStyled,
  BonusItemStyled,
  AccountListItemStyled as Item,
  AccountLinkStyled as Link,
  AccountButtonStyled as LinkButton,
  AccountListStyled as List,
  AccountListWrapperStyled as ListWrapper,
  AccountSubHeading as SubHeading,
} from './Account.styles'
import { trackActivityStatementsSelected } from './analytics'
import { useAccountDetailsQuery } from './helpers/useGetAccountDetails'

export const Account = (): JSX.Element | null => {
  const [isLoading, setIsLoading] = React.useState(true)

  const { accountNumber, bonusBetBalance, bonusCashBalance, campaigns } = useObservableImmutable(
    userAccountState$,
    ['accountNumber', 'bonusBetBalance', 'bonusCashBalance', 'campaigns']
  )
  const isAmlContactDetailsEnabled = useFeature(FeatureFlags.AML_CONTACT_DETAILS.key)

  React.useEffect(() => {
    const legacyModalSubscription = modalClose.signal$.subscribe(() => {
      queryClient.invalidateQueries({ queryKey: queryKeys.accountDetailsForMenu })
    })
    return () => {
      legacyModalSubscription.dispose()
    }
  }, [])

  const { data: accountDetails } = useAccountDetailsQuery(true)
  const { balance, blackBookAlertCount = 0 } = accountDetails || {}

  React.useEffect(() => {
    if (balance) setIsLoading(false)
  }, [balance])

  const bonusCash = bonusCashBalance ?? 0
  const bonusBet = bonusBetBalance ?? 0
  const campaignsCount = campaigns?.count() ?? 0

  return (
    <MainContainer background='grey'>
      <PageHeader title='My Account' />

      <AccountBalanceStyled>
        <span data-tid-my-account-balance>
          {isLoading ? <SpinnerInlineStyled color='dark' /> : balance}
        </span>
        <Button
          data-tid-my-account-deposit-plus-button
          onClick={() => openDeposit()}
          aria-label='Deposit'
        >
          <Icon type='plus' size='2rem' />
        </Button>
      </AccountBalanceStyled>

      {!isLoading && (bonusBet > 0 || bonusCash > 0) && (
        <BonusBalanceStyled>
          {bonusBet > 0 && (
            <BonusItemStyled>
              <BonusBalanceTitleStyled>Bonus Bets</BonusBalanceTitleStyled>
              <BonusBalanceValueStyled>
                <Money amount={bonusBet} decimalPlaces={2} data-tid-my-bonusbet-balance />
              </BonusBalanceValueStyled>
            </BonusItemStyled>
          )}

          {bonusCash > 0 && (
            <BonusItemStyled>
              <BonusBalanceTitleStyled>Bonus Cash</BonusBalanceTitleStyled>
              <BonusBalanceValueStyled>
                <Money amount={bonusCash} decimalPlaces={2} data-tid-my-bonuscash-balance />
              </BonusBalanceValueStyled>
            </BonusItemStyled>
          )}
        </BonusBalanceStyled>
      )}

      <ListWrapper>
        <SubHeading>Racing</SubHeading>
        <List>
          <Item>
            <Link to='/account/activity/pending' data-tid-my-account-pending>
              <Icon type='pending' /> Pending Bets
            </Link>
          </Item>

          <Item>
            <Link to='/account/activity/results' data-tid-my-account-resulted>
              <Icon type='resulted' /> Resulted Bets
            </Link>
          </Item>
        </List>
      </ListWrapper>

      <ListWrapper>
        <SubHeading>Sports</SubHeading>
        <List>
          <Item>
            <Link to='/tabtouch-sports/bethistory/open' data-tid-my-account-pending>
              <Icon type='pending' /> Pending Bets
            </Link>
          </Item>

          <Item>
            <Link to='/tabtouch-sports/bethistory/settled' data-tid-my-account-resulted>
              <Icon type='resulted' /> Resulted Bets
            </Link>
          </Item>
        </List>
      </ListWrapper>

      <ListWrapper>
        <SubHeading>Account</SubHeading>
        <List>
          <Item>
            <LinkButton
              data-tid-my-account-deposit
              to=''
              onClick={e => {
                e.preventDefault()
                openDeposit()
              }}
            >
              <Icon type='plus' /> Deposit
            </LinkButton>
          </Item>

          <Item>
            <Link to={WITHDRAW_ROOT_ROUTE} data-tid-my-account-withdrawal>
              <Icon type='minus' /> Withdraw
            </Link>
          </Item>

          <Item>
            <Link to='/account/activity/transactions' data-tid-my-account-credits-debits>
              <Icon type='statement' /> Credits / Debits
            </Link>
          </Item>

          {campaignsCount > 0 && (
            <Item>
              <Link to='/account/offer' data-tid-my-account-bonus>
                <Icon type='offer' /> Bonuses <Badge>{campaignsCount}</Badge>
              </Link>
            </Item>
          )}

          <Item>
            <Link to='/account/blackbook' data-tid-my-account-blackbook>
              <Icon type='blackbook' /> Blackbook{' '}
              {blackBookAlertCount > 0 && <Badge>{blackBookAlertCount}</Badge>}
            </Link>
          </Item>
        </List>
      </ListWrapper>

      <ListWrapper>
        <SubHeading>Responsible Wagering</SubHeading>
        <List>
          <Item>
            <Link to='/account/deposit-limits' data-tid-my-account-deposit-limits>
              <Icon type='depositLimits' /> Deposit Limit
            </Link>
          </Item>

          <Item>
            <Link to='/account/precommitment' data-tid-my-account-bet-limits>
              <Icon type='errorCircled' /> Bet Limit
            </Link>
          </Item>

          <Item>
            <Link
              to={`/${ACTIVITY_STATEMENTS_ROUTE}`}
              onClick={() => trackActivityStatementsSelected(accountNumber as number)}
            >
              <Icon type='statement' /> Statements
            </Link>
          </Item>

          <Item>
            <Link to={`/${TAKE_A_BREAK_ROUTE}`} data-tid-my-account-take-a-break>
              <Icon type='calendar' /> Take a Break
            </Link>
          </Item>

          <Item>
            <Link to='/account/close' data-tid-my-account-close-account>
              <Icon type='closeAccount' /> Close Account
            </Link>
          </Item>

          <Item>
            <Link to={accountRoutes.blockDebitCard} data-tid-my-account-block-debit-card>
              <Icon type='errorCircled' /> Block Debit Card
            </Link>
          </Item>
        </List>
      </ListWrapper>

      <ListWrapper>
        <SubHeading>Details</SubHeading>
        <List>
          <Item>
            <Link to='/account/change-password' data-tid-my-account-change-password>
              <Icon type='asterisk' /> Change Password
            </Link>
          </Item>
          <Item>
            <Link to='/account/settings' data-tid-my-account-settings>
              <Icon type='personMinimal' /> Settings
            </Link>
          </Item>
          <Item>
            <Link
              to={
                isAmlContactDetailsEnabled
                  ? accountRoutes.personalDetails
                  : '/account/contactdetails'
              }
              data-tid-my-account-contact-details
            >
              <Icon type='phone' /> Personal Details
            </Link>
          </Item>
          <Item>
            <Link to='/account/marketingcommunications' data-tid-my-account-subscription>
              <Icon type='envelopeOpen' /> Subscription
            </Link>
          </Item>
          {isReactNativeApp && (
            <Item>
              <Link to='/account/biometrics' data-tid-my-account-biometrics>
                <Icon type='depositLimits' /> Enable PIN/Biometrics
              </Link>
            </Item>
          )}
          <Item>
            <Link to='/contactus' data-tid-my-account-contactus>
              <Icon type='tabtouchLogoCompact' /> Contact Us
            </Link>
          </Item>
          <Item>
            <Link
              onClick={() => {
                trackEvent(keys.setPageVisit, { customURL: '/', pageTitle: 'HELP' })
              }}
              to='https://help.tabtouch.com.au/'
              data-tid-my-account-help
              rel='noopener'
              target='_blank'
            >
              <Icon type='infoCircled' /> Help & Info
            </Link>
          </Item>
        </List>
      </ListWrapper>

      <p style={{ marginBottom: 0, paddingBottom: spacing.md }}>
        Gamble responsibly. <br /> Call Gambling Help on {Constants.GamblingHelpPhoneNumber}.
      </p>
    </MainContainer>
  )
}
